import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useInfiniteList — pagination hook backed by an IntersectionObserver sentinel.
 *
 * @param {(skip:number, limit:number) => Promise<Array>} fetcher
 * @param {Array} deps  Values that reset the list & skip when they change (e.g. sort / query)
 * @param {number} pageSize
 */
export function useInfiniteList(fetcher, deps = [], pageSize = 4) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef(null);
  const skipRef = useRef(0);
  const inFlight = useRef(false);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Initial + reset when deps change
  useEffect(() => {
    let cancelled = false;
    skipRef.current = 0;
    setItems([]);
    setHasMore(true);
    setLoading(true);
    inFlight.current = true;
    fetcherRef.current(0, pageSize)
      .then((data) => {
        if (cancelled) return;
        setItems(data);
        skipRef.current = data.length;
        setHasMore(data.length >= pageSize);
      })
      .finally(() => {
        if (!cancelled) { setLoading(false); inFlight.current = false; }
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const loadMore = useCallback(async () => {
    if (inFlight.current || !hasMore) return;
    inFlight.current = true;
    setLoadingMore(true);
    try {
      const data = await fetcherRef.current(skipRef.current, pageSize);
      setItems((prev) => {
        // Dedupe by id in case of overlap on reload
        const seen = new Set(prev.map((x) => x.id));
        const fresh = data.filter((x) => !seen.has(x.id));
        return [...prev, ...fresh];
      });
      skipRef.current += data.length;
      if (data.length < pageSize) setHasMore(false);
    } finally {
      setLoadingMore(false);
      inFlight.current = false;
    }
  }, [hasMore, pageSize]);

  // Sentinel observer — reattach when list first renders or resets
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { rootMargin: "400px 0px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [loadMore, hasMore, loading, items.length]);

  return { items, loading, loadingMore, hasMore, sentinelRef };
}
